import { getRequiredCredits, handlePeopleCreditError } from '@/utils/credits';
import { message } from 'antd';
import { fetchPeopleListItemsV2 } from '../Search/people/service';
import { getPeopleCreditsAfterUnlock } from '../Search/people/sharedUnlock';
import {
  deletePeopleList,
  deletePeopleListItems,
  deleteProductList,
  deleteProductListItems,
  fetchDefaultPeopleListItems,
  fetchPhoneForListPerson,
  fetchProductListItems,
  renamePeopleList,
  renameProductList,
} from './service';

const Model = {
  namespace: 'list',
  state: {
    productLists: {},
    peopleLists: {},
    defaultPeopleList: {
      items: [],
      pagination: {
        total: 0,
        page: 1,
        pageSize: 10,
      },
    },
    selectedRows: [],
  },
  effects: {
    *fetchProductListItems({ payload, callback }: any, { call, put }: any) {
      let response: Promise<any> = yield call(fetchProductListItems, payload.id);

      response = (response as any).map((item: any) => {
        return {
          item_id: item?.id || null,
          id: item?.product?.id || null,
          product_id: item?.product?.id || null,
          name: item?.product?.name || '',
          packaging_type: item?.product?.packaging_type || '',
          country_of_origin: item?.product?.country_of_origin || '',
          application: item?.product?.application || '',
          category: item?.product?.category || '',
          sub_category: item?.product?.sub_category || '',
          marketed_by: item?.product?.marketer?.name || '-',
          marketer_employee_count: item?.product?.marketer?.team_size || '',
          manufactured_by: item?.product?.manufacturer?.name || '-',
          manufacturer_employee_count: item?.product?.manufacturer?.team_size || '',
          countries_of_sale:
            item?.product?.countries_of_sale?.map((c: any) => c?.name || '').join(', ') || '',
        };
      });
      yield put({
        type: 'setProductListItems',
        payload: {
          id: payload.id,
          items: response,
        },
      });

      if (callback) {
        callback(response);
      }
    },
    *fetchPeopleListItemsV2({ payload, callback }: any, { call, put }: any) {
      try {
        const response: any[] = yield call(fetchPeopleListItemsV2, payload.listId);

        console.log('response', response);

        yield put({
          type: 'setPeopleListItems',
          payload: {
            id: payload.listId,
            items: response,
          },
        });

        if (callback) {
          callback(response);
        }
      } catch (error) {
        console.error('Error fetching people list items:', error);
        if (callback) {
          callback([]);
        }
      }
    },
    *fetchDefaultPeopleListItems(
      { payload, callback }: any,
      { call, put }: any,
    ): Generator<any, void, any> {
      try {
        const { page = 1, pageSize = 10 } = payload || {};

        // Ensure page and pageSize are valid numbers
        const validPage = Math.max(1, Number(page) || 1);
        const validPageSize = Math.min(100, Math.max(1, Number(pageSize) || 10));

        const response = yield call(fetchDefaultPeopleListItems, {
          page: validPage,
          pageSize: validPageSize,
        });

        yield put({
          type: 'setDefaultPeopleListItems',
          payload: response,
        });

        if (callback) {
          callback(response);
        }
      } catch (error) {
        console.error('Error fetching default people list items:', error);
        if (callback) {
          callback({ items: [], pagination: { total: 0, page: 1, pageSize: 10 } });
        }
      }
    },
    *fetchPhoneForListPerson(
      { payload, callback }: any,
      { call, put, select }: any,
    ): Generator<any, void, any> {
      try {
        const subscriptionState = yield select((state: any) => state.subscription);
        const { people_credits_remaining } = subscriptionState;
        const phoneCreditCost = getRequiredCredits('phone', subscriptionState);
        const response = yield call(fetchPhoneForListPerson, payload);

        if (response.phone_numbers?.length === 0) {
          message.warning('No phone number found for this person');
        }

        if (callback) {
          callback(response);
        }

        if (response?.success) {
          yield put({
            type: 'subscription/deductCredits',
            payload: {
              people_credits_remaining: getPeopleCreditsAfterUnlock(
                response,
                people_credits_remaining,
                phoneCreditCost,
              ),
            },
          });
        }
      } catch (error) {
        console.error('Error fetching phone for list person:', error);
        handlePeopleCreditError(error, 'phone');
        if (callback) {
          callback({ success: false, phone_numbers: [] });
        }
      }
    },
    *renameProductList({ payload, callback }: any, { call, put }: any): Generator<any, void, any> {
      try {
        const response: any = yield call(renameProductList, payload.listId, payload.name);
        yield put({ type: 'search/fetchProductLists' });
        message.success('Product list renamed');
        if (callback) callback(response);
      } catch (error: any) {
        message.error(error?.response?.data?.message || 'Failed to rename product list');
        if (callback) callback(null);
      }
    },
    *deleteProductList({ payload, callback }: any, { call, put }: any): Generator<any, void, any> {
      try {
        const response: any = yield call(deleteProductList, payload.listId);
        yield put({ type: 'removeProductListCache', payload: payload.listId });
        yield put({ type: 'search/fetchProductLists' });
        message.success('Product list deleted');
        if (callback) callback(response);
      } catch (error: any) {
        message.error(error?.response?.data?.message || 'Failed to delete product list');
        if (callback) callback(null);
      }
    },
    *deleteProductListItems(
      { payload, callback }: any,
      { call, put }: any,
    ): Generator<any, void, any> {
      try {
        const response: any = yield call(deleteProductListItems, payload.listId, payload.itemIds);
        yield put({
          type: 'removeProductListItems',
          payload: { listId: payload.listId, itemIds: response.deletedItemIds || payload.itemIds },
        });
        yield put({ type: 'search/fetchProductLists' });
        message.success(
          `${response.deletedCount || payload.itemIds.length} product${
            (response.deletedCount || payload.itemIds.length) === 1 ? '' : 's'
          } removed`,
        );
        if (callback) callback(response);
      } catch (error: any) {
        message.error(error?.response?.data?.message || 'Failed to remove product list items');
        if (callback) callback(null);
      }
    },
    *renamePeopleList({ payload, callback }: any, { call, put }: any): Generator<any, void, any> {
      try {
        const response: any = yield call(renamePeopleList, payload.listId, payload.name);
        yield put({ type: 'people/fetchPeopleListsV2' });
        message.success('People list renamed');
        if (callback) callback(response);
      } catch (error: any) {
        message.error(error?.response?.data?.message || 'Failed to rename people list');
        if (callback) callback(null);
      }
    },
    *deletePeopleList({ payload, callback }: any, { call, put }: any): Generator<any, void, any> {
      try {
        const response: any = yield call(deletePeopleList, payload.listId);
        yield put({ type: 'removePeopleListCache', payload: payload.listId });
        yield put({ type: 'people/fetchPeopleListsV2' });
        message.success('People list deleted');
        if (callback) callback(response);
      } catch (error: any) {
        message.error(error?.response?.data?.message || 'Failed to delete people list');
        if (callback) callback(null);
      }
    },
    *deletePeopleListItems(
      { payload, callback }: any,
      { call, put }: any,
    ): Generator<any, void, any> {
      try {
        const response: any = yield call(deletePeopleListItems, payload.listId, payload.itemIds);
        yield put({
          type: 'removePeopleListItems',
          payload: { listId: payload.listId, itemIds: response.deletedItemIds || payload.itemIds },
        });
        yield put({ type: 'people/fetchPeopleListsV2' });
        message.success(
          `${response.deletedCount || payload.itemIds.length} ${
            (response.deletedCount || payload.itemIds.length) === 1 ? 'person' : 'people'
          } removed`,
        );
        if (callback) callback(response);
      } catch (error: any) {
        message.error(error?.response?.data?.message || 'Failed to remove people list items');
        if (callback) callback(null);
      }
    },
  },
  reducers: {
    setProductListItems(state: any, { payload }: any) {
      return {
        ...state,
        productLists: {
          ...state.productLists,
          [payload.id]: payload.items,
        },
      };
    },
    setPeopleListItems(state: any, { payload }: any) {
      return {
        ...state,
        peopleLists: {
          ...state.peopleLists,
          [payload.id]: payload.items,
        },
      };
    },
    setDefaultPeopleListItems(state: any, { payload }: any) {
      return {
        ...state,
        defaultPeopleList: payload,
      };
    },
    removeProductListCache(state: any, { payload }: any) {
      const productLists = { ...state.productLists };
      delete productLists[payload];
      return { ...state, productLists, selectedRows: [] };
    },
    removePeopleListCache(state: any, { payload }: any) {
      const peopleLists = { ...state.peopleLists };
      delete peopleLists[payload];
      return { ...state, peopleLists };
    },
    removeProductListItems(state: any, { payload }: any) {
      const deletedIds = new Set(payload.itemIds.map(Number));
      const currentItems = state.productLists[payload.listId] || [];
      const deletedProductIds = new Set(
        currentItems
          .filter((item: any) => deletedIds.has(Number(item.item_id)))
          .map((item: any) => Number(item.id)),
      );
      return {
        ...state,
        productLists: {
          ...state.productLists,
          [payload.listId]: currentItems.filter(
            (item: any) => !deletedIds.has(Number(item.item_id)),
          ),
        },
        selectedRows: state.selectedRows.filter((id: any) => !deletedProductIds.has(Number(id))),
      };
    },
    removePeopleListItems(state: any, { payload }: any) {
      const deletedIds = new Set(payload.itemIds.map(Number));
      const currentItems = state.peopleLists[payload.listId] || [];
      return {
        ...state,
        peopleLists: {
          ...state.peopleLists,
          [payload.listId]: currentItems.filter((item: any) => !deletedIds.has(Number(item.id))),
        },
      };
    },
    initSelectedRows(state: any) {
      return {
        ...state,
        selectedRows: [],
      };
    },
    addToSelectedRows(state: any, { payload }: any) {
      if (!state.selectedRows.includes(payload)) {
        return {
          ...state,
          selectedRows: [...state.selectedRows, payload],
        };
      }
      return state;
    },
    removeFromSelectedRows(state: any, { payload }: any) {
      return {
        ...state,
        selectedRows: state.selectedRows.filter((id: any) => id !== payload),
      };
    },
    clearSelectedRows(state: any) {
      return {
        ...state,
        selectedRows: [],
      };
    },
  },
};

export default Model;
